import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  fetchTeamBoards,
  fetchTeamBoard,
  fetchTeamBoardTasks,
  createTeamBoard,
  updateTeamBoard,
  deleteTeamBoard,
  fetchOrCreateTeamBoardForSpace,
  fetchOrCreateTeamBoardForLead,
  setLeadTaskBoard,
  createTeamBoardColumn,
  updateTeamBoardColumn,
  deleteTeamBoardColumn,
  reorderTeamBoardColumns,
  createTeamBoardCard,
  createTeamBoardLinkedCard,
  updateTeamBoardCard,
  moveTeamBoardCard,
  deleteTeamBoardCard,
  createTeamBoardLabel,
  createTeamBoardCardChecklist,
  updateTeamBoardCardChecklist,
  deleteTeamBoardCardChecklist,
  createTeamBoardChecklistItem,
  updateTeamBoardChecklistItem,
  deleteTeamBoardChecklistItem,
  createTeamBoardCardAttachment,
  deleteTeamBoardCardAttachment,
  createTeamBoardCardComment,
  updateTeamBoardCardComment,
  deleteTeamBoardCardComment,
  addTeamBoardMember,
  updateTeamBoardMemberRole,
  removeTeamBoardMember,
  fetchLeadRequiredTasks,
  fetchLeadTasks,
} from "../api/client";

export const TEAM_BOARDS_KEY = ["teamBoards"];
export const TEAM_BOARD_TASKS_KEY = ["teamBoardTasks"];
export const TEAM_LEAD_TASKS_KEY = ["teamLeadTasks"];
export const teamBoardKey = (id) => ["teamBoard", id];
export const teamBoardTasksKey = (filters = {}) => ["teamBoardTasks", filters];
export const teamLeadTasksKey = (leadId) => ["teamLeadTasks", leadId];

/** Mantém a visão filtrada por solicitação quando o painel é compartilhado. */
export function filterBoardPayloadForLead(board, leadId) {
  if (!board || !leadId) return board;
  const lid = Number(leadId);
  return {
    ...board,
    filterByLead: true,
    contextLeadId: lid,
    columns: (board.columns || []).map((col) => ({
      ...col,
      cards: (col.cards || []).filter((card) => Number(card.leadId) === lid),
    })),
  };
}

export function useTeamBoardsList({ spaceId, leadId, workspace, enabled = true } = {}) {
  return useQuery(
    [...TEAM_BOARDS_KEY, { spaceId, leadId, workspace }],
    () => fetchTeamBoards({ spaceId, leadId, workspace }),
    { staleTime: 30_000, enabled }
  );
}

export function useTeamBoard(boardId, { enabled = true, contextLeadId = null } = {}) {
  const leadKey = contextLeadId != null && Number(contextLeadId) > 0 ? Number(contextLeadId) : null;
  return useQuery(
    leadKey ? [...teamBoardKey(boardId), "lead", leadKey] : teamBoardKey(boardId),
    () => fetchTeamBoard(boardId, { contextLeadId: leadKey }),
    { enabled: Boolean(boardId) && enabled, staleTime: 10_000 }
  );
}

export function useTeamBoardForSpace(spaceId, { enabled = true } = {}) {
  return useQuery(
    ["teamBoardForSpace", spaceId],
    () => fetchOrCreateTeamBoardForSpace(spaceId),
    { enabled: Boolean(spaceId) && enabled, staleTime: 10_000 }
  );
}

export function useTeamBoardForLead(leadId, { enabled = true } = {}) {
  return useQuery(
    ["teamBoardForLead", leadId],
    () => fetchOrCreateTeamBoardForLead(leadId),
    { enabled: Boolean(leadId) && enabled, staleTime: 10_000 }
  );
}

export function useSetLeadTaskBoard(leadId) {
  const queryClient = useQueryClient();
  return useMutation(
    (boardId) => setLeadTaskBoard(leadId, boardId),
    {
      onSuccess: (board) => {
        if (leadId) {
          queryClient.setQueryData(["teamBoardForLead", leadId], board);
        }
        if (board?.id) {
          queryClient.setQueryData(teamBoardKey(board.id), board);
        }
        queryClient.invalidateQueries(TEAM_BOARDS_KEY);
        queryClient.invalidateQueries(TEAM_BOARD_TASKS_KEY);
      },
    }
  );
}

export function useLeadRequiredTasks(leadId, { enabled = true } = {}) {
  return useQuery(
    ["leadRequiredTasks", leadId],
    () => fetchLeadRequiredTasks(leadId),
    { enabled: Boolean(leadId) && enabled, staleTime: 15_000 }
  );
}

export function useLeadTasks(leadId, { enabled = true } = {}) {
  return useQuery(
    teamLeadTasksKey(leadId),
    () => fetchLeadTasks(leadId),
    { enabled: Boolean(leadId) && enabled, staleTime: 10_000 }
  );
}

export function useTeamBoardTasks(filters = {}, { enabled = true } = {}) {
  return useQuery(
    teamBoardTasksKey(filters),
    () => fetchTeamBoardTasks(filters),
    { enabled, staleTime: 20_000 }
  );
}

export function useTeamBoardMutations(boardId, { extraCacheKeys = [], contextLeadId = null } = {}) {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries(TEAM_BOARDS_KEY);
    queryClient.invalidateQueries(TEAM_BOARD_TASKS_KEY);
    queryClient.invalidateQueries(TEAM_LEAD_TASKS_KEY);
    if (boardId) queryClient.invalidateQueries(teamBoardKey(boardId));
  };

  const setBoardCache = (board) => {
    if (!board?.id) return;
    let cached = board;
    if (contextLeadId) {
      cached = filterBoardPayloadForLead(
        {
          ...board,
          boardSource: board.boardSource,
          leadBoardId: board.leadBoardId,
          tipoBoardId: board.tipoBoardId,
        },
        contextLeadId
      );
    }
    const mergeCached = (old) => {
      if (!old) return cached;
      return {
        ...cached,
        boardSource: cached.boardSource ?? old.boardSource,
        leadBoardId: cached.leadBoardId ?? old.leadBoardId,
        tipoBoardId: cached.tipoBoardId ?? old.tipoBoardId,
      };
    };
    queryClient.setQueryData(teamBoardKey(board.id), mergeCached);
    for (const key of extraCacheKeys) {
      // Não sobrescrever caches que não são payload de board (ex.: lista de tasks).
      if (Array.isArray(key) && key[0] === TEAM_LEAD_TASKS_KEY[0]) continue;
      if (Array.isArray(key) && key[0] === TEAM_BOARD_TASKS_KEY[0]) continue;
      queryClient.setQueryData(key, mergeCached);
    }
  };

  const mergeMembersCache = (members) => {
    if (!boardId || !members) return;
    const memberCount = members.length;
    const patch = (old) => (old ? { ...old, members, memberCount } : old);
    queryClient.setQueryData(teamBoardKey(boardId), patch);
    for (const key of extraCacheKeys) {
      queryClient.setQueryData(key, patch);
    }
    queryClient.setQueriesData(TEAM_BOARDS_KEY, (old) => {
      if (!Array.isArray(old)) return old;
      return old.map((board) =>
        Number(board.id) === Number(boardId) ? { ...board, members, memberCount } : board
      );
    });
  };

  const withLeadContext = (payload = {}) =>
    contextLeadId
      ? { ...payload, leadId: payload.leadId ?? contextLeadId, contextLeadId }
      : payload;

  return {
    createBoard: useMutation(createTeamBoard, { onSuccess: invalidate }),
    updateBoard: useMutation(
      (payload) => updateTeamBoard(boardId, payload),
      { onSuccess: (data) => { setBoardCache(data); invalidate(); } }
    ),
    deleteBoard: useMutation(() => deleteTeamBoard(boardId), { onSuccess: invalidate }),
    createColumn: useMutation(
      (payload) => createTeamBoardColumn(boardId, payload),
      { onSuccess: (data) => { setBoardCache(data); } }
    ),
    updateColumn: useMutation(
      ({ columnId, ...payload }) => updateTeamBoardColumn(boardId, columnId, payload),
      { onSuccess: (data) => { setBoardCache(data); } }
    ),
    deleteColumn: useMutation(
      (columnId) => deleteTeamBoardColumn(boardId, columnId),
      { onSuccess: (data) => { setBoardCache(data); } }
    ),
    reorderColumns: useMutation(
      (columnIds) => reorderTeamBoardColumns(boardId, columnIds),
      { onSuccess: (data) => { setBoardCache(data); } }
    ),
    createCard: useMutation(
      (payload) => createTeamBoardCard(boardId, withLeadContext(payload)),
      {
        onSuccess: (data) => {
          setBoardCache(data);
          queryClient.invalidateQueries(TEAM_LEAD_TASKS_KEY);
        },
      }
    ),
    createLinkedCard: useMutation(
      ({ cardId, ...payload }) =>
        createTeamBoardLinkedCard(boardId, cardId, withLeadContext(payload)),
      {
        onSuccess: (data) => {
          // API devolve o painel da tarefa pai (source) com children atualizados.
          if (data?.id) {
            setBoardCache(data);
          }
          const sourceId =
            data?.sourceBoardId != null ? Number(data.sourceBoardId) : Number(boardId);
          const targetId =
            data?.targetBoardId != null
              ? Number(data.targetBoardId)
              : data?.id != null
                ? Number(data.id)
                : null;

          if (sourceId) {
            queryClient.invalidateQueries(teamBoardKey(sourceId));
            queryClient.invalidateQueries(["teamBoardForLead"]);
          }
          if (targetId && targetId !== sourceId) {
            queryClient.invalidateQueries(teamBoardKey(targetId));
          }
          queryClient.invalidateQueries(TEAM_BOARD_TASKS_KEY);
          queryClient.invalidateQueries(TEAM_LEAD_TASKS_KEY);
        },
      }
    ),
    updateCard: useMutation(
      ({ cardId, ...payload }) => updateTeamBoardCard(boardId, cardId, withLeadContext(payload)),
      {
        onSuccess: (data) => {
          setBoardCache(data);
          queryClient.invalidateQueries(TEAM_LEAD_TASKS_KEY);
        },
      }
    ),
    moveCard: useMutation(
      ({ cardId, columnId, position }) =>
        moveTeamBoardCard(boardId, cardId, withLeadContext({ columnId, position })),
      {
        onSuccess: (data) => {
          setBoardCache(data);
          queryClient.invalidateQueries(TEAM_LEAD_TASKS_KEY);
        },
      }
    ),
    deleteCard: useMutation(
      (cardId) =>
        deleteTeamBoardCard(
          boardId,
          cardId,
          contextLeadId ? { contextLeadId } : undefined
        ),
      {
        onSuccess: (data) => {
          setBoardCache(data);
          queryClient.invalidateQueries(TEAM_LEAD_TASKS_KEY);
        },
      }
    ),
    createLabel: useMutation(
      (payload) => createTeamBoardLabel(boardId, payload),
      { onSuccess: (data) => { setBoardCache(data); } }
    ),
    createChecklist: useMutation(
      ({ cardId, ...payload }) => createTeamBoardCardChecklist(boardId, cardId, payload),
      { onSuccess: (data) => { setBoardCache(data); } }
    ),
    updateChecklist: useMutation(
      ({ cardId, checklistId, ...payload }) =>
        updateTeamBoardCardChecklist(boardId, cardId, checklistId, payload),
      { onSuccess: (data) => { setBoardCache(data); } }
    ),
    deleteChecklist: useMutation(
      ({ cardId, checklistId }) => deleteTeamBoardCardChecklist(boardId, cardId, checklistId),
      { onSuccess: (data) => { setBoardCache(data); } }
    ),
    createChecklistItem: useMutation(
      ({ cardId, checklistId, ...payload }) =>
        createTeamBoardChecklistItem(boardId, cardId, checklistId, payload),
      { onSuccess: (data) => { setBoardCache(data); } }
    ),
    updateChecklistItem: useMutation(
      ({ cardId, itemId, ...payload }) =>
        updateTeamBoardChecklistItem(boardId, cardId, itemId, payload),
      { onSuccess: (data) => { setBoardCache(data); } }
    ),
    deleteChecklistItem: useMutation(
      ({ cardId, itemId }) => deleteTeamBoardChecklistItem(boardId, cardId, itemId),
      { onSuccess: (data) => { setBoardCache(data); } }
    ),
    createAttachment: useMutation(
      ({ cardId, ...payload }) => createTeamBoardCardAttachment(boardId, cardId, payload),
      { onSuccess: (data) => { setBoardCache(data); } }
    ),
    deleteAttachment: useMutation(
      ({ cardId, attachmentId }) => deleteTeamBoardCardAttachment(boardId, cardId, attachmentId),
      { onSuccess: (data) => { setBoardCache(data); } }
    ),
    createComment: useMutation(
      ({ cardId, ...payload }) => createTeamBoardCardComment(boardId, cardId, payload),
      { onSuccess: (data) => { setBoardCache(data); } }
    ),
    updateComment: useMutation(
      ({ cardId, commentId, ...payload }) =>
        updateTeamBoardCardComment(boardId, cardId, commentId, payload),
      { onSuccess: (data) => { setBoardCache(data); } }
    ),
    deleteComment: useMutation(
      ({ cardId, commentId }) => deleteTeamBoardCardComment(boardId, cardId, commentId),
      { onSuccess: (data) => { setBoardCache(data); } }
    ),
    addMember: useMutation(
      (payload) => addTeamBoardMember(boardId, payload),
      { onSuccess: (members) => { mergeMembersCache(members); } }
    ),
    updateMemberRole: useMutation(
      ({ memberId, role }) => updateTeamBoardMemberRole(boardId, memberId, role),
      { onSuccess: (members) => { mergeMembersCache(members); } }
    ),
    removeMember: useMutation(
      (memberId) => removeTeamBoardMember(boardId, memberId),
      { onSuccess: (members) => { mergeMembersCache(members); } }
    ),
    invalidate,
    setBoardCache,
  };
}
